import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Box,
} from "@mui/material";

interface DoctorSpecialityProps {
  data: {
    name: string;
    count?: number;
    associatedCount?: number;
    v1?: number;
    v2?: number;
    v3?: number;
  }[];
  isLimited?: boolean;
}

const DoctorSpeciality: React.FC<DoctorSpecialityProps> = ({
  data,
  isLimited,
}) => {
  const usesCount = data.some((row) => row.count !== undefined);

  const maxV1 = usesCount
    ? Math.max(...data.map((row) => row.count || 0))
    : Math.max(...data.map((row) => row.v1 || 0));
  const maxV2 = isLimited ? 0 : Math.max(...data.map((row) => row.v2 || 0));
  const maxV3 = isLimited ? 0 : Math.max(...data.map((row) => row.v3 || 0));

  return (
    <TableContainer
      sx={{
        maxWidth: "50%",
        margin: "0 auto",
        height: "373px",
        overflow: "auto",
      }}
    >
      {!isLimited ? (
        <Typography variant="h6" align="center" gutterBottom>
          Frequency Wise Doctor Count
        </Typography>
      ) : (
        <Typography variant="h6" align="center" gutterBottom>
          Speciality Wise Doctor Count
        </Typography>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "10%" }}></TableCell>
            {isLimited ? (
              <TableCell sx={{ width: "26.66%" }} align="center">
                .
              </TableCell>
            ) : (
              <>
                <TableCell sx={{ width: "26.66%" }} align="center">
                  1V
                </TableCell>
                <TableCell sx={{ width: "26.66%" }} align="center">
                  2V
                </TableCell>
                <TableCell sx={{ width: "26.66%" }} align="center">
                  3V
                </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => {
            const v1Width = ((usesCount ? row.count! : row.v1!) / maxV1) * 70;
            const v2Width = !isLimited && row.v2 ? (row.v2 / maxV2) * 70 : 0;
            const v3Width = !isLimited && row.v3 ? (row.v3 / maxV3) * 70 : 0;

            return (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    width: "20%",
                    fontSize: "14px",
                    padding: "4px",
                    textTransform: "uppercase",
                  }}
                >
                  {row.name}
                </TableCell>
                <TableCell sx={{ width: "26.66%" }} align="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "left",
                      gap: "3px",
                    }}
                  >
                    <Box sx={{ width: `${v1Width}%`, minWidth: "20px" }}>
                      <LinearProgress
                        variant="determinate"
                        value={100}
                        sx={{
                          height: 15,
                          borderRadius: 0,
                          backgroundColor: "transparent",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "#2A5782",
                            borderRadius: 0,
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2">
                      {usesCount
                        ? `${row.count ?? 0}/${row.associatedCount ?? 0}`
                        : row.v1 ?? 0}
                    </Typography>
                  </Box>
                </TableCell>
                {!isLimited && (
                  <>
                    <TableCell sx={{ width: "26.66%" }} align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "left",
                          gap: "3px",
                        }}
                      >
                        <Box sx={{ width: `${v2Width}%`, minWidth: "20px" }}>
                          <LinearProgress
                            variant="determinate"
                            value={100}
                            sx={{
                              height: 15,
                              borderRadius: 0,
                              backgroundColor: "transparent",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#2A5782",
                                borderRadius: 0,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="body2">{row.v2}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: "26.66%" }} align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "left",
                          gap: "3px",
                        }}
                      >
                        <Box sx={{ width: `${v3Width}%`, minWidth: "20px" }}>
                          <LinearProgress
                            variant="determinate"
                            value={100}
                            sx={{
                              height: 15,
                              borderRadius: 0,
                              backgroundColor: "transparent",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#2A5782",
                                borderRadius: 0,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="body2">{row.v3}</Typography>
                      </Box>
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DoctorSpeciality;
